import { RefObject, useEffect, useRef, useState } from "react"
import { ConfigProvider, DatePicker, DatePickerProps } from "antd"
import { RangePickerProps } from "antd/es/date-picker"
import dayjs, { Dayjs } from "dayjs"
import styled from "styled-components"

import { useOutsideClick } from "utils/useOutsideClick"
import { useWindowResize } from "hooks/useWindowResize"

import { ButtonNew } from "../../components/form/ButtonNew"

const { RangePicker } = DatePicker

const Wrapper = styled.div`
  position: relative;
  z-index: 10;
`

const Container = styled.div<{ isMobile: boolean; setRight: boolean; $leftOffset?: number }>`
  display: flex;
  margin-left: 5px;
  flex-direction: column-reverse;
  align-items: flex-start;
  padding-bottom: 14px;
  position: absolute;
  border-radius: 8px;
  width: 580px;
  height: 375px;
  background-color: #fff;
  box-shadow:
    0 6px 16px 0 rgba(0, 0, 0, 0.08),
    0 3px 6px -4px rgba(0, 0, 0, 0.12),
    0 9px 28px 8px rgba(0, 0, 0, 0.05);

  ${({ setRight }) =>
    setRight &&
    `
    right: 10px;
    `}

  ${({ $leftOffset }) =>
    $leftOffset !== undefined &&
    `
    left: ${$leftOffset}px;
    right: auto;
    `}

  > div {
    position: relative;
    padding-bottom: 0;
  }

  div {
    box-shadow: none !important;
  }

  td {
    padding: 2px 0 !important;
  }

  .ant-picker {
    position: relative;
    height: 32px;
    padding-bottom: 3px;
    z-index: 10000;
  }

  .ant-picker-dropdown {
    inset: initial !important;
  }

  .ant-picker-body {
    padding: 6px !important;
  }

  ${({ isMobile }) =>
    isMobile &&
    `
  width: 290px; 
  height: 395px;
  padding-bottom: 7px;

  .ant-picker-panel:nth-child(2) {
    display: none;
  }

  .ant-picker-panel:nth-child(1) button {
    visibility: visible !important;
  }
    `}
`

const RangePickerContainer = styled.div<{ isMobile: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px 0 12px;
  flex-wrap: wrap;
  width: 100%;

  > div:last-child {
    min-width: 200px;
  }

  ${({ isMobile }) =>
    isMobile &&
    `
     width: 100%;
     flex-wrap: wrap;
     row-gap: 6px;
     padding: 0 8px;
     margin: 0;

     .ant-picker-range {
       flex: 1;
     }
    `}
`

const ButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-left: auto;

  button {
    font-weight: 400;
  }
`

interface DateRangePickerProps<T extends boolean = false> {
  maxRange?: number
  minDate?: string
  selectedRange?: T extends true ? [Dayjs | null, Dayjs | null] : [Dayjs, Dayjs]
  isMobile?: boolean
  allowNulls?: T
  scrollIntoView?: boolean
  onSubmit: (dateRange: T extends true ? [Dayjs | null, Dayjs | null] : [Dayjs, Dayjs]) => void
  onCancel: () => void
}

const DateRangePicker = <T extends boolean = false>({
  maxRange,
  minDate,
  selectedRange,
  isMobile = false,
  allowNulls = false as T,
  scrollIntoView = false,
  onSubmit,
  onCancel,
}: DateRangePickerProps<T>) => {
  const [applyDisabled, setApplyDisabled] = useState(!selectedRange)
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null)
  const [setRight, setSetRight] = useState(false)
  const [leftOffset, setLeftOffset] = useState<number | undefined>(undefined)
  const container: RefObject<HTMLDivElement | null> = useRef(null)
  const isLargeScreen = useWindowResize()
  const showMobile = isMobile || !isLargeScreen

  useOutsideClick(container, onCancel)

  useEffect(() => {
    if (scrollIntoView && container.current) {
      container.current.style.scrollMargin = "10px"

      container.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest",
      })
    }
  }, [scrollIntoView, container.current])

  useEffect(() => {
    if (!container.current || !scrollIntoView) return

    const getScrollParent = (el: HTMLElement): HTMLElement => {
      let parent = el.parentElement
      while (parent) {
        const overflow = getComputedStyle(parent).overflowX
        if (overflow === "auto" || overflow === "scroll") return parent
        parent = parent.parentElement
      }
      return document.documentElement
    }

    const checkPosition = () => {
      if (!container.current) return

      const wrapperEl = container.current.parentElement
      if (!wrapperEl) return

      const wrapperRect = wrapperEl.getBoundingClientRect()
      const scrollParent = getScrollParent(wrapperEl)
      const scrollRect = scrollParent.getBoundingClientRect()
      const pickerWidth = showMobile ? 290 : 580
      const threshold = 50
      const padding = 8

      const availableRight = scrollRect.right - wrapperRect.left
      const overflowsRight = availableRight - threshold < pickerWidth
      setSetRight(overflowsRight)

      if (overflowsRight) {
        const rightAlignedLeft = wrapperRect.right - pickerWidth - 10
        if (rightAlignedLeft < scrollRect.left + padding) {
          setLeftOffset(scrollRect.left - wrapperRect.left + padding)
        } else {
          setLeftOffset(undefined)
        }
      } else {
        setLeftOffset(undefined)
      }
    }

    checkPosition()
    window.addEventListener("resize", checkPosition)

    return () => {
      window.removeEventListener("resize", checkPosition)
    }
  }, [])

  const disabledRange: DatePickerProps["disabledDate"] = (current, { from }) => {
    if (current > dayjs().endOf("day")) {
      return true
    }

    if (minDate && current < dayjs(minDate).startOf("day")) {
      return true
    }

    if (from && maxRange) {
      return Math.abs(current.diff(from, "days")) >= maxRange
    }

    return false
  }

  const handleChange: RangePickerProps["onChange"] = dateRange => {
    if (!dateRange?.[0] || !dateRange?.[1]) {
      setApplyDisabled(true)
      setDateRange(null)
      return
    }

    if (dateRange?.length && (!maxRange || dateRange[1].diff(dateRange[0], "days") < maxRange)) {
      setApplyDisabled(false)
      setDateRange([dateRange[0] as Dayjs, dateRange[1] as Dayjs])
    } else {
      setApplyDisabled(true)
      setDateRange(null)
    }
  }

  const handleCalendarChange: RangePickerProps["onCalendarChange"] = dates => {
    if (allowNulls) {
      setApplyDisabled(dates?.[0] || dates?.[1] ? false : true)
      setDateRange([(dates?.[0] as Dayjs) || null, (dates?.[1] as Dayjs) || null])
    }
  }

  const handleSubmit = () => {
    if (dateRange) {
      onSubmit(dateRange as T extends true ? [Dayjs | null, Dayjs | null] : [Dayjs, Dayjs])
    }
  }

  return (
    <Wrapper>
      <Container ref={container} isMobile={showMobile} setRight={setRight} $leftOffset={leftOffset}>
        <RangePickerContainer isMobile={showMobile}>
          <ConfigProvider
            theme={{
              components: {
                DatePicker: {
                  cellHeight: 32,
                },
              },
              token: {
                motion: false,
                colorPrimary: "#43e0f8",
                colorPrimaryBg: "#ebfcff",
              },
            }}
          >
            <RangePicker
              open
              placement="topLeft"
              defaultValue={selectedRange}
              getPopupContainer={() => container.current!}
              disabledDate={maxRange || minDate ? disabledRange : undefined}
              onChange={handleChange}
              onCalendarChange={handleCalendarChange}
              size="middle"
            />
          </ConfigProvider>
          <ButtonsContainer>
            <ButtonNew onClick={onCancel} size={"auto"} color="greyBordered">
              Cancel
            </ButtonNew>
            <ButtonNew onClick={handleSubmit} size={"auto"} disabled={applyDisabled} color={"blue"}>
              Apply
            </ButtonNew>
          </ButtonsContainer>
        </RangePickerContainer>
      </Container>
    </Wrapper>
  )
}

export default DateRangePicker
